/**
 * Shared (non-component) metadata + humanizers for the Books & Records panel.
 * Kept separate from chips.tsx so that file only exports components
 * (react-refresh / fast-refresh requirement).
 */

import {
  CheckCircle2,
  FlagTriangleRight,
  CircleAlert,
  MinusCircle,
  Lock,
  KeyRound,
  Ban,
  Clock,
  type LucideIcon,
} from "lucide-react";
import type { BadgeColor } from "@/ui/primitives/badge";
import type { CompletenessReason, ItemState } from "./types";

export const STATE_META: Record<
  ItemState,
  { label: string; icon: LucideIcon; color: BadgeColor; icon_color: string }
> = {
  present: {
    label: "In Morgan",
    icon: CheckCircle2,
    color: "emerald",
    icon_color: "text-emerald-600",
  },
  held_offline: {
    label: "Held Offline",
    icon: FlagTriangleRight,
    color: "sky",
    icon_color: "text-sky-600",
  },
  outstanding: {
    label: "Outstanding",
    icon: CircleAlert,
    color: "red",
    icon_color: "text-red-600",
  },
  not_applicable: {
    label: "N/A",
    icon: MinusCircle,
    color: "gray",
    icon_color: "text-muted-foreground",
  },
};

/** The caveat shown on every verdict — it is always keyed to the upload. */
export const COMPLETENESS_CAVEAT =
  "Based on the books and records uploaded to Morgan.";

/**
 * Per-reason presentation + recommended next step for when the practitioner
 * says the uploaded files are NOT the complete set. The recommendation is
 * deterministic (not AI-generated): withheld → ASIC assistance; records don't
 * exist → s533 report; the remaining reasons get sensible follow-up guidance.
 */
export const COMPLETENESS_REASON_META: Record<
  CompletenessReason,
  {
    label: string;
    icon: LucideIcon;
    recommendation: string;
    /** Tone of the recommendation callout. */
    tone: "warning" | "info";
  }
> = {
  withheld: {
    label: "Director is withholding them",
    icon: Lock,
    recommendation:
      "Records exist but are being withheld. Recommend requesting ASIC assistance to compel their production.",
    tone: "warning",
  },
  no_access: {
    label: "Director has no access",
    icon: KeyRound,
    recommendation:
      "The director cannot access the records. Consider requesting ASIC assistance and pursuing third-party holders (former accountant, bookkeeper or bank).",
    tone: "warning",
  },
  do_not_exist: {
    label: "Records don't exist",
    icon: Ban,
    recommendation:
      "Records don't exist. Flag this for the s533 report (possible offence / failure to keep records).",
    tone: "warning",
  },
  not_yet_received: {
    label: "Not yet received",
    icon: Clock,
    recommendation:
      "Records are expected but not yet received. Follow up with the director and third parties. The verdict will keep updating automatically as documents arrive.",
    tone: "info",
  },
};

const TABLE_LABELS: Record<string, string> = {
  loanEntries: "Loan",
  securityInterestEntries: "Security Interest",
  assetEntries: "Asset",
  employeeEntries: "Employee",
  liabilityEntries: "Creditor",
  relatedPartyEntries: "Related Party",
};

export function humanizeTable(table: string): string {
  return TABLE_LABELS[table] ?? table.replace(/Entries$/, "");
}

/** "No_Documentation" → "No Documentation", "company" → "Company". */
export function humanizeCode(code: string): string {
  return code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
